import { LitElement, html } from 'lit'
import { ModalBaseMixin } from '../core/mixins/modal-base.mixin.js'

/**
 * Modal para gerenciamento de parcelas de transações
 * Permite visualizar, editar e excluir parcelas individuais
 *
 * Layout: flexbox com header fixo, conteúdo com scroll independente e footer fixo
 * com botões de ação sempre visíveis (padrão 040-modal-scroll-layout-padronizado)
 */
class ModalParcelas extends ModalBaseMixin(LitElement) {
  static properties = {
    transaction: { type: Object },
    installments: { type: Array },
    _saving: { type: Boolean, state: true }
  }

  createRenderRoot() {
    return this
  }

  constructor() {
    super()
    this.open = false
    this.transaction = null
    this.installments = []
    this._saving = false
  }

  /**
   * Fecha o modal delegando ao mixin base.
   * O mixin gerencia open=false, disparo de evento modal-closed
   * e integração com History API.
   * Reseta o estado de loading para garantir estado limpo na próxima abertura.
   *
   * @param {object} [options={}] - Opções de fechamento repassadas ao mixin base
   * @param {boolean} [options.fromPopstate=false] - Indica se veio do popstate
   */
  close (options = {}) {
    this._saving = false
    super.close(options)
  }

  show(transaction) {
    this.transaction = transaction
    this._generateInstallments()
    this.open = true
  }

  _generateInstallments() {
    if (!this.transaction?.installment) return

    const { current, total } = this.transaction.installment
    const totalAmount = Math.abs(this.transaction.amount)
    const installmentValue = totalAmount / total
    
    this.installments = Array.from({ length: total }, (_, index) => {
      const installmentNumber = index + 1
      const date = this._calculateInstallmentDate(this.transaction.date, index)
      
      return {
        number: installmentNumber,
        date,
        amount: installmentValue,
        value: installmentValue,
        isCurrent: installmentNumber === current
      }
    })
  }

  _calculateInstallmentDate(baseDate, monthsToAdd) {
    try {
      const [day, month, year] = baseDate.split('/')
      const originalDay = parseInt(day)
      const originalMonth = parseInt(month) - 1
      const originalYear = parseInt(year)
      
      if (isNaN(originalDay) || isNaN(originalMonth) || isNaN(originalYear)) {
        return 'Data inválida'
      }
      
      let newMonth = originalMonth + monthsToAdd
      let newYear = originalYear
      
      while (newMonth >= 12) {
        newMonth -= 12
        newYear++
      }
      
      while (newMonth < 0) {
        newMonth += 12
        newYear--
      }
      
      const lastDayOfMonth = new Date(newYear, newMonth + 1, 0).getDate()
      const finalDay = Math.min(originalDay, lastDayOfMonth)
      
      const finalDate = new Date(newYear, newMonth, finalDay)
      return finalDate.toLocaleDateString('pt-BR')
    } catch (error) {
      return 'Data inválida'
    }
  }

  _formatCurrency(amount) {
    const isNegative = amount < 0
    const absAmount = Math.abs(amount)
    const formatted = absAmount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
    
    return isNegative ? `R$ -${formatted.replace('R$ ', '')}` : formatted
  }

  _editInstallment(installmentNumber) {
    const installment = this.installments.find(i => i.number === installmentNumber)
    if (installment) {
      const installmentTransaction = {
        ...this.transaction,
        date: installment.date,
        amount: this.transaction.amount >= 0 ? installment.value : -installment.value
      }
      
      this.close()
      this.dispatchEvent(new CustomEvent('edit-installment', {
        detail: { transaction: installmentTransaction, installmentNumber },
        bubbles: true
      }))
    }
  }

  _deleteInstallment(installmentNumber) {
    if (!this.transaction) {
      return
    }
    
    if (confirm(`Tem certeza que deseja excluir a parcela ${installmentNumber}?`)) {
      this.dispatchEvent(new CustomEvent('delete-installment', {
        detail: { transactionId: this.transaction.id, installmentNumber },
        bubbles: true
      }))
    }
  }

  _deleteAllInstallments() {
    if (!this.transaction) {
      return
    }
    
    if (confirm('Tem certeza que deseja excluir todas as parcelas? Esta ação não pode ser desfeita.')) {
      this.dispatchEvent(new CustomEvent('delete-all-installments', {
        detail: { transactionId: this.transaction.id },
        bubbles: true
      }))
      this.close()
    }
  }

  /**
   * Executa a operação de salvamento com controle de loading state.
   * Desabilita botões durante a operação e restaura após conclusão.
   * Guard contra clique duplo: se já está salvando, ignora chamadas subsequentes.
   * No modal de parcelas, o save dispara o evento de exclusão de todas as parcelas.
   * @returns {Promise<void>}
   */
  async _handleSave() {
    if (this._saving) return

    if (!this.transaction) return

    this._saving = true

    try {
      this.dispatchEvent(new CustomEvent('delete-all-installments', {
        detail: { transactionId: this.transaction.id },
        bubbles: true
      }))

      this.close()
    } finally {
      this._saving = false
    }
  }

  /**
   * Fecha o modal ao clicar no backdrop (área escurecida)
   * Delega para this.close() que já gerencia o estado do modal
   */
  _handleBackdropClick() {
    this.close()
  }

  /**
   * Impede propagação do clique no card para o backdrop
   * Evita que cliques dentro do modal disparem o fechamento
   * @param {Event} e - Evento de clique
   */
  _stopPropagation(e) {
    e.stopPropagation()
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Salvar
   * Replica o padrão do floating-button com transição suave de cor
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onSaveHover(e) {
    e.target.style.backgroundColor = 'var(--hover-primary, #7B3EF0)'
  }

  /**
   * Restaura cor original do botão Salvar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onSaveLeave(e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Cancelar
   * Usa background secundário para destacar o botão neutro
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onCancelHover(e) {
    e.target.style.backgroundColor = 'var(--glass-bg, rgba(117, 133, 255, 0.22))'
  }

  /**
   * Restaura cor original do botão Cancelar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onCancelLeave(e) {
    e.target.style.backgroundColor = ''
  }

  render() {
    if (!this.transaction) return html``

    const totalValue = this.installments.length > 0 ? 
      this.installments[0].value * this.installments.length : 0

    return html`
      <div 
        class="modal ${this.open ? 'is-active' : ''}" 
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 30000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 700px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
          "
        >
          <!-- Zona 1: Header fixo no topo com sombra sobre o conteúdo -->
          <header class="card-header" style="flex-shrink: 0; position: relative; z-index: 2; box-shadow: var(--shadow-header-modal, 0 4px 12px rgba(0,0,0,0.3));">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-parcelas-title">
              Gerenciar Parcelas - ${this.transaction.description} (${this.transaction.installment?.current}/${this.transaction.installment?.total})
            </p>
            ${this.renderBotaoFechar('modal-parcelas-close')}
          </header>

          <!-- Zona 2: Conteúdo com scroll independente -->
          <div class="card-content custom-scrollbar" style="
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            min-height: 0;
          ">
            <p class="subtitle is-6 has-text-grey mb-4">Visualize e gerencie as parcelas desta transação.</p>

            <!-- Informações Gerais -->
            <div class="card mb-5">
              <div class="card-content">
                <div class="columns is-multiline">
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Tipo</p>
                    <p class="has-text-weight-medium">Parcelamento</p>
                  </div>
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Total de Parcelas</p>
                    <p class="has-text-weight-medium">${this.transaction.installment?.total}</p>
                  </div>
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Valor Total</p>
                    <p class="has-text-weight-medium">${this._formatCurrency(totalValue)}</p>
                  </div>
                  <div class="column is-6">
                    <p class="has-text-grey is-size-7">Valor da Parcela</p>
                    <p class="has-text-weight-medium">${this._formatCurrency(Math.abs(this.transaction.amount))}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Lista de Parcelas -->
            <div class="mb-4">
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                <h3 class="title is-6">Parcelas</h3>
                <button 
                  class="button is-danger is-small"
                  data-cy="btn-delete-all-installments"
                  @click="${this._deleteAllInstallments}"
                >
                  <span class="icon is-small">
                    <i class="fas fa-trash"></i>
                  </span>
                  <span>Excluir Todas as Parcelas</span>
                </button>
              </div>

              <div class="space-y-2">
                ${this.installments.map(installment => html`
                  <div class="card">
                    <div class="card-content p-4">
                      <div class="is-flex is-justify-content-space-between is-align-items-center">
                        <div class="is-flex-grow-1">
                          <div class="is-flex is-align-items-center mb-1" style="gap: 0.5rem;">
                            <span class="tag is-warning is-light">${installment.number}/${this.transaction.installment?.total}</span>
                            <span>${installment.date}</span>
                            ${installment.isCurrent ? html`
                              <span class="tag is-success is-light">Atual</span>
                            ` : ''}
                          </div>
                          <p class="has-text-grey">${this._formatCurrency(installment.value)}</p>
                        </div>
                        <div class="is-flex" style="gap: 0.5rem;">
                          <button 
                            class="button is-small is-ghost"
                            data-cy="btn-edit-installment"
                            @click="${() => this._editInstallment(installment.number)}"
                          >
                            <span class="icon is-small">
                              <i class="fas fa-pen"></i>
                            </span>
                          </button>
                          <button 
                            class="button is-small is-ghost"
                            data-cy="btn-delete-installment"
                            @click="${() => this._deleteInstallment(installment.number)}"
                          >
                            <span class="icon is-small">
                              <i class="fas fa-trash"></i>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                `)}
              </div>
            </div>
          </div>

          <!-- Zona 3: Footer fixo com botões de ação e sombra sobre o conteúdo -->
          <footer class="card-footer"
            data-cy="footer-modal-parcelas"
            aria-label="Ações do modal de parcelas"
            style="
              flex-shrink: 0;
              padding: 1rem 1.5rem;
              background-color: var(--bg-surface, #151C30);
              border-top: 1px solid var(--border, #26304A);
              position: relative;
              z-index: 2;
              box-shadow: var(--shadow-footer-modal, 0 -4px 12px rgba(0,0,0,0.3));
            "
          >
            <div class="field is-grouped is-justify-content-flex-end" style="width: 100%">
              <div class="control">
                <button 
                  class="button" 
                  type="button" 
                  data-cy="btn-close-parcelas"
                  ?disabled="${this._saving}"
                  @click="${this.close}"
                  @mouseenter="${this._onCancelHover}"
                  @mouseleave="${this._onCancelLeave}"
                >
                  Fechar
                </button>
              </div>
              <div class="control">
                <button 
                  class="button is-primary ${this._saving ? 'is-loading' : ''}" 
                  type="button" 
                  data-cy="btn-salvar-parcelas"
                  ?disabled="${this._saving}"
                  @click="${this._handleSave}"
                  @mouseenter="${this._onSaveHover}"
                  @mouseleave="${this._onSaveLeave}"
                >
                  Salvar
                </button>
              </div>
            </div>
          </footer>
        </div>
      </div>
    `
  }
}

customElements.define('modal-parcelas', ModalParcelas)
